import { pure } from '@core/helpers/pure';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  TuiDialogContext,
  TuiError,
  TuiGroup,
  TuiButton,
  TuiCheckbox,
  TuiRadio,
  TuiTextfield,
} from '@taiga-ui/core';
import {
  TuiBlock,
  TuiInputNumber,
  TuiTextarea,
  TuiSkeleton,
} from '@taiga-ui/kit';
import {
  RequestDealDetailsExtended,
  RequestDealDetailsInstallingEnv,
} from '@core/types/RequestDealDetails';

import { ActualDeliveryAddressGroupActionsViewComponent } from '@requestGroupActions/components';

import { Nullable } from '@core/helpers/nullable';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { TimePickerComponent } from '@core/components/time-picker/time-picker.component';
import { RequestService } from '@core/services/request/request.service';
import { AsyncPipe, CommonModule, NgClass } from '@angular/common';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RequestDealDetailsService } from '@core/services/request';
import {
  DeviceDetectorService,
  IsLoadingService,
  XAlertService,
} from '@core/services';
import { AccountService } from '@core/services/account/account.service';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { RequestStatusDealDetailsForm } from '@features/requests/request-form/components/request-form-status/types';
import { RequestFormActualAddressComponent } from '../request-form-actual-address/request-form-actual-address.component';
import { RequestFormRatingsComponent } from '../request-form-ratings/request-form-ratings.component';
import { TranslatePipe } from '@ngx-translate/core';
import { EmptyBlockComponent } from '@core/components/empty-block/empty-block.component';
import {
  PRIMARY_DOCUMENTATION,
  type NullablePrimaryDocumentation,
} from '@outkeep/shared';
import {
  getPrimaryDocumentationTranslationKey,
  isPrimaryDocumentationRequired,
} from './request-form-deal-details.helpers';

enum FormFields {
  InstallerTime = 'installerTime',
  SubjTime = 'subjTime',
  ReleaseStartTime = 'releaseStartTime',
  CompletedTime = 'completedTime',
  InstallingEnv = 'installingEnv',
  ThirdParties = 'thirdParties',
  DocumentsGiven = 'documentsGiven',
  KeysGiven = 'keysGiven',
  NothingGiven = 'nothingGiven',
  Problems = 'problems',
  DemountedDevicesCount = 'demountedDevicesCount',
  PrimaryDocumentation = 'primaryDocumentation',
}

@Component({
  selector: 'x-request-form-deal-details',
  imports: [
    TuiSkeleton,
    TuiTextfield,
    ReactiveFormsModule,
    TuiTextarea,
    TuiError,
    TuiBlock,
    TuiRadio,
    TuiCheckbox,
    TuiButton,
    TuiGroup,
    TimePickerComponent,
    AsyncPipe,
    CommonModule,
    NgClass,
    ActualDeliveryAddressGroupActionsViewComponent,
    RequestFormRatingsComponent,
    RequestFormActualAddressComponent,
    TranslatePipe,
    FormsModule,
    TuiInputNumber,
    EmptyBlockComponent,
  ],
  templateUrl: './request-form-deal-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestFormDealDetailsComponent implements OnInit {
  private readonly context = inject<
    TuiDialogContext<Nullable<RequestStatusDealDetailsForm>> | undefined
  >(POLYMORPHEUS_CONTEXT, { optional: true });

  private readonly requestService = inject(RequestService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly accountService = inject(AccountService);
  private readonly xAlertService = inject(XAlertService);
  private readonly destroyRef = inject(DestroyRef);
  readonly isLoading$: BehaviorSubject<boolean> = inject(IsLoadingService);

  public readonly permissionService = inject(OutkeepPermissionService);

  readonly dealDetailsService = inject(RequestDealDetailsService);
  readonly deviceDetectorService = inject(DeviceDetectorService);

  $isInsideSummary = input<boolean>(false, { alias: 'isInsideSummary' });
  $isInsideWorkflow = input<boolean>(false, { alias: 'isInsideWorkflow' });
  $dealDetails = input<Nullable<RequestDealDetailsExtended>>(null, {
    alias: 'dealDetails',
  });

  $groupActionMode = input<boolean>(false, { alias: 'groupActionMode' });
  $groupActionsViewObserver = input<Subject<never> | null>(null, {
    alias: 'viewObserver',
  });
  $canExecuteWorkflow = input<boolean>(true, { alias: 'canExecuteWorkflow' });

  readonly $request = this.requestService.$request;
  private $selectedRequests = this.requestService.selectedRequests;
  readonly $isPrimaryDocumentationRequired = computed(() =>
    isPrimaryDocumentationRequired(
      this.$groupActionMode() ? this.$selectedRequests() : [this.$request()],
    ),
  );
  readonly FormFields = FormFields;
  private readonly formGroupData = {
    [FormFields.InstallerTime]: this.getDefaultInstallerTimeFieldValue(),
    [FormFields.SubjTime]: this.getDefaultSubjTimeFieldValue(),
    [FormFields.ReleaseStartTime]: this.getDefaultReleaseStartTimeFieldValue(),
    [FormFields.CompletedTime]: this.getDefaultCompletedTimeFieldValue(),
    [FormFields.InstallingEnv]: null,
    [FormFields.ThirdParties]: false,
    [FormFields.DocumentsGiven]: false,
    [FormFields.KeysGiven]: false,
    [FormFields.NothingGiven]: false,
    [FormFields.Problems]: '',
    [FormFields.DemountedDevicesCount]: null,
    [FormFields.PrimaryDocumentation]: null as NullablePrimaryDocumentation,
  };

  public hasDemountedDevices = signal(false);

  readonly formGroup = this.formBuilder.group<RequestStatusDealDetailsForm>(
    this.formGroupData,
  );

  readonly RequestDealDetailsInstallingEnv = RequestDealDetailsInstallingEnv;
  readonly PrimaryDocumentation = PRIMARY_DOCUMENTATION;
  public isCreateDialog = !!this.context;

  public dealDetails: Nullable<RequestDealDetailsExtended> = null;

  readonly user = this.accountService.$currentUser();

  constructor() {
    effect(() => {
      if (this.$groupActionMode()) {
        if (this.$selectedRequests().length > 0) {
          // reinit form time fields
          this.formGroup.reset(this.formGroupData);
        }
      }
    });
    effect(() => {
      if (this.hasDemountedDevices()) {
        this.demountedDevicesCountField.addValidators(Validators.required);
      } else {
        if (this.demountedDevicesCountField.hasValidator(Validators.required)) {
          this.demountedDevicesCountField.removeValidators(Validators.required);
          this.demountedDevicesCountField.reset();
        }
      }
    });
    effect(() => {
      if (this.$isPrimaryDocumentationRequired()) {
        this.primaryDocumentationField.addValidators(Validators.required);
      } else {
        this.primaryDocumentationField.removeValidators(Validators.required);
      }

      this.primaryDocumentationField.updateValueAndValidity({
        emitEvent: false,
      });
    });
    effect(() => {
      if (this.$isInsideSummary() && this.$dealDetails()) {
        this.dealDetails = this.$dealDetails();
        if (this.dealDetails?.details) {
          this.formGroup.patchValue(this.dealDetails.details);
          if (this.dealDetails.details.demountedDevicesCount) {
            this.hasDemountedDevices.set(true);
          }
        }
      }
    });
    effect(() => {
      if (!this.$isInsideWorkflow()) {
        return;
      }

      if (this.$canExecuteWorkflow()) {
        this.formGroup.enable({ emitEvent: false });
        this.applyConditionalDisableRules();
      } else {
        this.formGroup.disable({ emitEvent: false });
      }
    });
  }

  ngOnInit() {
    this.init();
  }

  private init() {
    if (
      !this.isCreateDialog &&
      !this.$groupActionMode() &&
      !this.$isInsideSummary()
    ) {
      this.dealDetailsService.requestDealDetails$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(dealDetails => {
          if (dealDetails?.details) {
            this.dealDetails = dealDetails;
            this.formGroup.patchValue(dealDetails.details);
            if (dealDetails.details.demountedDevicesCount) {
              this.hasDemountedDevices.set(true);
            }
          }
        });
    }
    if (this.$groupActionMode()) {
      this.dealDetailsService
        .getDetailsForSelectedRequests$(this.$groupActionsViewObserver()!)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
    }
    this.handleFormValueChanges();

    this.checkUserPermissionsToEdit();

    this.installerTimeField.addValidators(Validators.required);
    this.subjTimeField.addValidators(Validators.required);
    this.releaseStartTimeField.addValidators(Validators.required);
    this.completedTimeField.addValidators(Validators.required);
    this.installingEnvField.addValidators(Validators.required);
    this.problemsField.addValidators(Validators.maxLength(10_000));

    if (this.$isInsideWorkflow()) {
      this.dealDetailsService.currentDealDetailsForm =
        this.formGroup.getRawValue() as RequestStatusDealDetailsForm;
    }
  }

  private checkUserPermissionsToEdit() {
    if (this.isCreateDialog || this.$isInsideWorkflow()) return;

    this.permissionService.canEditDealDetails$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(canEditDealDetails => {
        if (!canEditDealDetails) {
          this.formGroup.disable();
        }
      });
  }

  private handleFormValueChanges() {
    this.formGroup.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.formGroup.disabled) {
          this.applyConditionalDisableRules();
        }

        // сохраняем текущее состояние формы в сервисе
        this.dealDetailsService.currentDealDetailsForm =
          this.formGroup.getRawValue() as RequestStatusDealDetailsForm;
      });
  }

  private applyConditionalDisableRules() {
    const formValue = this.formGroup.getRawValue();
    const keysGiven = formValue[FormFields.KeysGiven];
    const documentsGiven = formValue[FormFields.DocumentsGiven];

    if (keysGiven || documentsGiven) {
      this.formGroup.controls[FormFields.NothingGiven].disable({
        emitEvent: false,
      });
      if (this.nothingGivenField.value) {
        this.nothingGivenField.setValue(false, { emitEvent: false });
      }

      return;
    }

    this.formGroup.controls[FormFields.NothingGiven].enable({
      emitEvent: false,
    });
  }

  private getRequestReleaseTimeWithHoursOffset(
    hoursOffset: number,
  ): Nullable<string> {
    let releaseTime;

    if (this.$groupActionMode()) {
      releaseTime = this.$selectedRequests()[0].releaseTime;
    } else {
      releaseTime = this.$request()?.releaseTime;
    }

    if (releaseTime) {
      const time = releaseTime.split(':');
      let hours = time[0] ? +time[0] : 12;
      const minutes = time[1] ? +time[1] : 0;

      if (time[0]) {
        hours += hoursOffset;
        // Убедимся, что часы находятся в диапазоне от 0 до 23
        hours = ((hours % 24) + 24) % 24;
      }

      return `${hours}`.padStart(2, '0') + ':' + `${minutes}`.padStart(2, '0');
    }

    return null;
  }

  submit() {
    const formData =
      this.formGroup.getRawValue() as RequestStatusDealDetailsForm;

    if (this.isCreateDialog) {
      this.context?.completeWith(formData);
    } else {
      this.dealDetailsService
        .updateRequestDealDetails$(formData, this.$groupActionMode())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.xAlertService.success('Данные успешно сохранены');
          // очищаем текущее состояние формы в сервисе
          this.dealDetailsService.currentDealDetailsForm = null;
          if (this.$groupActionMode()) {
            this.$groupActionsViewObserver()!.complete();
          }
        });
    }
  }

  dismiss() {
    this.context?.completeWith(null);
  }

  private getDefaultInstallerTimeFieldValue() {
    return this.getRequestReleaseTimeWithHoursOffset(-1);
  }

  private getDefaultSubjTimeFieldValue() {
    return this.getRequestReleaseTimeWithHoursOffset(0);
  }

  private getDefaultReleaseStartTimeFieldValue() {
    return this.getRequestReleaseTimeWithHoursOffset(0);
  }

  private getDefaultCompletedTimeFieldValue() {
    return this.getRequestReleaseTimeWithHoursOffset(1);
  }

  get primaryDocumentationTranslationKey() {
    return getPrimaryDocumentationTranslationKey(
      this.primaryDocumentationField.getRawValue(),
    );
  }

  @pure
  get installerTimeField() {
    return this.formGroup.controls[FormFields.InstallerTime];
  }

  @pure
  get subjTimeField() {
    return this.formGroup.controls[FormFields.SubjTime];
  }

  @pure
  get releaseStartTimeField() {
    return this.formGroup.controls[FormFields.ReleaseStartTime];
  }

  @pure
  get completedTimeField() {
    return this.formGroup.controls[FormFields.CompletedTime];
  }

  @pure
  get installingEnvField() {
    return this.formGroup.controls[FormFields.InstallingEnv];
  }

  @pure
  get problemsField() {
    return this.formGroup.controls[FormFields.Problems];
  }

  @pure
  get keysGivenField() {
    return this.formGroup.controls[FormFields.KeysGiven];
  }

  @pure
  get documentsGivenField() {
    return this.formGroup.controls[FormFields.DocumentsGiven];
  }

  @pure
  get nothingGivenField() {
    return this.formGroup.controls[FormFields.NothingGiven];
  }

  @pure
  get demountedDevicesCountField() {
    return this.formGroup.controls[FormFields.DemountedDevicesCount];
  }

  @pure
  get primaryDocumentationField() {
    return this.formGroup.controls[FormFields.PrimaryDocumentation];
  }

  @pure
  get thirdPartiesField() {
    return this.formGroup.controls[FormFields.ThirdParties];
  }

  @pure
  get dealDuration() {
    return this.dealDetails?.details?.durationInHours;
  }
}
